/**
 * ExploreCardSkeleton — loading placeholder matching ExploreWheelCard's shape.
 * Rendered in a grid while the first page (or next page) is in flight.
 *
 * Variants:
 *   - default : `count` shells laid out in the MUI responsive Grid.
 *   - inline  : a single bare shell for one virtualized grid cell (no Grid).
 *   - compact : `count` fixed-width (168px) shells for the DL-024 showcase rail's
 *               horizontal row — same footprint as the compact ExploreWheelCard so
 *               the rail does not change height when real cards replace the
 *               skeletons (no layout shift of the grid below).
 *   - hero    : a single bare LANDSCAPE shell matching `ExploreHeroCard` (DL-095):
 *               round wheel left, text block right. Added because the Discover
 *               banner stopped being a portrait card on 2026-08-02 — a 4:3
 *               portrait placeholder in a landscape cell is a visible jump when
 *               the real cards arrive, which is the layout shift the other
 *               variants exist to avoid.
 */

import React from "react";
import PropTypes from "prop-types";
import { Grid, Skeleton, Box, Stack } from "@mui/material";

// Keep in sync with ExploreWheelCard's rail widths.
const RAIL_WIDTH = 168;

// The card-shaped shell, reused by the MUI-Grid, virtualized (`inline`), and
// rail (`rail`) variants so the paths never drift. `rail` fixes the width;
// `dense` uses the compact padding/avatar scale (2-up phone cells + rail cards).
const SkeletonShell = ({ rail = false, dense = false }) => {
  const tight = rail || dense;
  return (
    <Box
      aria-hidden="true"
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
        overflow: "hidden",
        height: "100%",
        ...(rail ? { width: `${RAIL_WIDTH}px`, flex: `0 0 ${RAIL_WIDTH}px` } : {}),
      }}
    >
      <Skeleton
        variant="rectangular"
        sx={{ width: "100%", aspectRatio: "4 / 3" }}
      />
      <Box sx={{ p: tight ? "10px 12px 12px" : "12px 14px 14px" }}>
        <Skeleton variant="text" width="85%" height={22} />
        <Skeleton variant="text" width="55%" height={22} />
        <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
          <Skeleton
            variant="circular"
            width={tight ? 22 : 24}
            height={tight ? 22 : 24}
          />
          <Skeleton variant="text" width="40%" height={18} />
        </Stack>
        <Stack direction="row" gap={tight ? 0.75 : 2} sx={{ mt: 1.5 }}>
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
          <Skeleton variant="text" width={28} height={16} />
        </Stack>
      </Box>
    </Box>
  );
};

SkeletonShell.propTypes = {
  rail: PropTypes.bool,
  dense: PropTypes.bool,
};

// Landscape shell for the Discover banner (DL-095). Same border/radius family as
// SkeletonShell so the two read as one system; the INTERNAL layout mirrors
// ExploreHeroCard (36% wheel column + text panel) rather than the grid card.
const HeroSkeletonShell = () => (
  <Box
    aria-hidden="true"
    data-testid="explore-hero-card-skeleton"
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "12px",
      overflow: "hidden",
      height: "100%",
      boxSizing: "border-box",
      p: "12px",
      display: "grid",
      gridTemplateColumns: "minmax(0, 36%) minmax(0, 1fr)",
      gap: "12px",
      alignItems: "center",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Skeleton
        variant="circular"
        sx={{ width: "min(100%, 200px)", aspectRatio: "1 / 1", height: "auto" }}
      />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Skeleton variant="text" width="80%" height={26} />
      <Skeleton variant="text" width="95%" height={18} />
      <Skeleton variant="text" width="90%" height={18} />
      <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
        <Skeleton variant="text" width={28} height={16} />
        <Skeleton variant="text" width={28} height={16} />
        <Skeleton variant="text" width={28} height={16} />
      </Stack>
    </Box>
  </Box>
);

function ExploreCardSkeleton({
  count = 8,
  inline = false,
  compact = false,
  dense = false,
  hero = false,
  phoneSingleColumn = false,
}) {
  // `hero`: a single bare LANDSCAPE shell for one Discover banner cell.
  if (hero) return <HeroSkeletonShell />;

  // `inline`: a single bare shell for one virtualized grid cell (no MUI Grid).
  if (inline) return <SkeletonShell dense={dense} />;

  // `compact`: bare fixed-width shells for the horizontal showcase rail (the
  // rail itself is the flex row; no MUI Grid wrapping).
  if (compact) {
    return (
      <>
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonShell rail dense key={`explore-skeleton-compact-${i}`} />
        ))}
      </>
    );
  }

  // Default: MUI responsive Grid mirroring the virtual grid's column plan
  // (§5.6): 2-up phone · 3-up tabletP · 4-up tabletL/desktop · 5-up wide.
  // T22.229: `phoneSingleColumn` mirrors the grid's 1-up phone opt-in so the
  // loading shells don't jump from 2-up to 1-up when the cards arrive.
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Grid
          item
          xs={phoneSingleColumn ? 12 : 6}
          sm={4}
          md={3}
          lg={3}
          xl={2.4}
          key={`explore-skeleton-${i}`}
        >
          <SkeletonShell dense={dense} />
        </Grid>
      ))}
    </>
  );
}

ExploreCardSkeleton.propTypes = {
  count: PropTypes.number,
  inline: PropTypes.bool,
  compact: PropTypes.bool,
  dense: PropTypes.bool,
  // Single landscape shell matching ExploreHeroCard (Discover banner, DL-095).
  hero: PropTypes.bool,
  // T22.229: 1-up phone shells (matches ExploreVirtualGrid phoneSingleColumn).
  phoneSingleColumn: PropTypes.bool,
};

export default ExploreCardSkeleton;
